import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { getLoanAddresses } from '@dmi/shared/redux/Main/selectors';

import { capitalizeString, formatErrorMessages } from '../../../utils/globalHelpers';
import {
  CANADA,
  INTERNATIONAL,
  REQUIRED_CANADIAN_FIELDS,
  REQUIRED_DOMESTIC_FIELDS,
  REQUIRED_INTERNATIONAL_FIELDS,
} from './constants';
import {
  formatDomesticMailingAddress,
  formatInternationalMailingAddress,
  formatReadOnlyFields,
} from './helpers';
import { initialState } from './reducer';
import makeSelectMain from '../../Main/selectors';

/**
 * Direct selector to the Loans state domain
 */
const selectLoansDomain = (state) => state.loans || initialState;

const selectBannerError = () => createSelector(
  makeSelectLoans('errors'),
  ({ bannerError }) => bannerError,
);

const selectCanUploadMailingAddress = () => createSelector(
  makeSelectLoans('loanData'),
  makeSelectLoans('selectedLoan'),
  (loanData, selectedLoan) => (!!loanData[selectedLoan] && loanData[selectedLoan].canUpload),
);

const selectEditableMailingAddress = () => createSelector(
  makeSelectLoans('loanData'),
  makeSelectLoans('allowedCountries'),
  (loans, countries) => {
    if (isEmpty(loans)) return {};
    return Object.entries(loans).reduce((acc, [loanNumber, loanData]) => {
      acc[loanNumber] = {};
      acc[loanNumber].pending = !!loanData.pendingSetting;
      const { currentSetting: currentMailingAddress } = loanData || {};
      if (currentMailingAddress) {
        const { addressIsForeign } = currentMailingAddress;
        if (addressIsForeign) {
          acc[loanNumber].mailingAddress =
            formatInternationalMailingAddress(currentMailingAddress, countries);
        } else {
          acc[loanNumber].mailingAddress = formatDomesticMailingAddress(currentMailingAddress);
        }
      } else {
        acc[loanNumber].mailingAddress = {};
      }

      acc[loanNumber].canUpload = loanData.canUpload;
      acc[loanNumber].isLoanMancodeY = loanData.isLoanMancodeY;

      if (loanData.status === 'Address_Change_Eligible') {
        acc[loanNumber].canEditOnline = true;
      } else {
        acc[loanNumber].canEditOnline = false;
      }
      return acc;
    }, {});
  },
);

const selectForm = (prop) => createSelector(
  makeSelectLoans('forms'),
  (forms) => forms[prop],
);

const selectFormattedCurrentSetting = () => createSelector(
  makeSelectLoans('loanData'),
  makeSelectLoans('selectedLoan'),
  (loanData, selectedLoan) => {
    const formattedCurrentSetting = {};
    if (selectedLoan) {
      const { currentSetting } = loanData[selectedLoan] || {};
      if (currentSetting) {
        formattedCurrentSetting.addressLine2 = currentSetting.addressLine2;
        formattedCurrentSetting.apartment = currentSetting.addressLine4;
        formattedCurrentSetting.city = currentSetting.addressCity;
        formattedCurrentSetting.company = currentSetting.addressLine3;
        formattedCurrentSetting.state = currentSetting.addressState;
        formattedCurrentSetting.streetAddress = currentSetting.addressLine5;
        formattedCurrentSetting.zip = currentSetting.addressPostalCode;
      }
    }
    return formattedCurrentSetting;
  },
);

const selectFormattedReadOnlyFields = () => createSelector(
  makeSelectLoans('loanData'),
  makeSelectLoans('selectedLoan'),
  (loanData, selectedLoan) => {
    if (!isEmpty(loanData) && selectedLoan) {
      const { readOnlyFields } = loanData[selectedLoan];
      return formatReadOnlyFields(readOnlyFields);
    }
    return [];
  },
);

const selectFormErrors = (prop) => createSelector(
  makeSelectLoans('formErrors'),
  (formErrors) => formErrors[prop],
);

const selectFormToUse = () => createSelector(
  selectInternationalFormType(),
  ({ isAddressInternational }) => (
    isAddressInternational ? 'changeMailingAddressInternational' : 'changeMailingAddress'
  ),
);

const selectFormValuesToSubmit = () => createSelector(
  selectInternationalFormType(),
  selectForm('changeMailingAddress'),
  selectForm('changeMailingAddressInternational'),
  selectMailingAddressAddressee(),
  (internationalFormType, domesticFormFields, internationalFormFields, addressee) => {
    const { isAddressCanadian, isAddressInternational } = internationalFormType;
    if (isAddressCanadian) {
      return { addressee, ...internationalFormFields };
    }
    if (isAddressInternational) {
      const { province, ...restBody } = internationalFormFields;
      return { addressee, ...restBody };
    }
    return { addressee, country: 'US', ...domesticFormFields };
  },
);

const selectInternationalFormType = () => createSelector(
  makeSelectLoans('changeMailingAddressFormType'),
  selectForm('changeMailingAddressInternational'),
  (formType, internationalFormFields) => {
    const { country } = internationalFormFields;
    const isAddressInternational = formType === INTERNATIONAL;
    const isAddressCanadian = country === CANADA;

    return { isAddressCanadian, isAddressInternational };
  },
);

const selectIsCountryProvinceStateDataFetched = () => createSelector(
  makeSelectLoans('allowedCountries'),
  makeSelectLoans('allowedProvinces'),
  makeSelectLoans('allowedStates'),
  (countries, provinces, states) => !!countries.length && !!provinces.length && !!states.length,
);

const selectIsPastServicingPaymentCutOffDate = () => createSelector(
  makeSelectLoans('transferStatus'),
  ({ servicingPaymentCutOffDate }) => {
    if (servicingPaymentCutOffDate) {
      return moment.utc().isSameOrAfter(moment.utc(servicingPaymentCutOffDate));
    }
    return false;
  },
);

const selectIsSubmitMailingAddressChangeButtonDisabled = () => createSelector(
  selectFormErrors('changeMailingAddress'),
  selectFormErrors('changeMailingAddressInternational'),
  selectRequestBody(),
  selectForm('changeMailingAddressInternational'),
  selectInternationalFormType(),
  (
    domesticFormErrors,
    internationalFormErrors,
    domesticRequestBody,
    internationalRequestBody,
    { isAddressCanadian, isAddressInternational },
  ) => {
    const hasDomesticErrors = Object.values(domesticFormErrors).some((value) => value);
    const hasInternationalErrors = Object.values(internationalFormErrors).some((value) => value);
    const requiredCanadianFields = [...REQUIRED_CANADIAN_FIELDS, ...REQUIRED_INTERNATIONAL_FIELDS];

    if (isAddressCanadian) {
      return (
        !requiredCanadianFields.every((field) => internationalRequestBody[field]) ||
        hasInternationalErrors
      );
    }
    if (isAddressInternational) {
      return (
        !REQUIRED_INTERNATIONAL_FIELDS.every((field) => internationalRequestBody[field]) ||
        hasInternationalErrors
      );
    }
    return (
      !REQUIRED_DOMESTIC_FIELDS.every((field) => domesticRequestBody[field]) ||
      hasDomesticErrors
    );
  },
);

const selectLoanMailingAddress = (prop) => createSelector(
  makeSelectLoans('loanData'),
  makeSelectLoans('selectedLoan'),
  makeSelectLoans('allowedCountries'),
  (loanData, selectedLoan, countries) => {
    let mailingAddress = {};
    const formattedProp = `${prop}Setting`;
    if (selectedLoan) {
      const selectedLoanData = loanData[selectedLoan] || {};
      const addressData = selectedLoanData[formattedProp] || {};
      const { addressIsForeign } = addressData;
      if (addressIsForeign) {
        mailingAddress = formatInternationalMailingAddress(addressData, countries);
      } else {
        mailingAddress = formatDomesticMailingAddress(addressData);
      }
    }
    return mailingAddress;
  },
);

const selectLoansScreenReaderNotifications = () => createSelector(
  selectBannerError(),
  makeSelectLoans('errors'),
  makeSelectLoans('formErrors'),
  (
    bannerError,
    {
      serviceErrors: {
        addLoan: addLoanServiceError,
        mailingAddress: mailindAddressServiceError,
      },
    },
    { addLoan, changeMailingAddress, uploadDocument },
  ) => {
    const nonFormErrors = [bannerError, addLoanServiceError, mailindAddressServiceError];
    const notifications = nonFormErrors.filter((error) => !!error);
    return notifications.concat(formatErrorMessages({
      ...addLoan,
      ...changeMailingAddress,
      ...uploadDocument,
    }));
  },
);

const selectMailingAddressAddressee = () => createSelector(
  makeSelectLoans('loanData'),
  makeSelectLoans('selectedLoan'),
  (loanData, selectedLoan) => {
    if (selectedLoan) {
      const { currentSetting: currentMailingAddress } = loanData[selectedLoan] || {};
      if (currentMailingAddress) {
        const { addressLine2: mailingAddressAddressee } = currentMailingAddress;
        return mailingAddressAddressee;
      }
    }
    return '';
  },
);

const selectIsMailingAddressCancelable = () => createSelector(
  makeSelectLoans('loanData'),
  makeSelectLoans('selectedLoan'),
  (loanData, selectedLoan) => {
    if (
      isEmpty(loanData) ||
      !selectedLoan ||
      isEmpty(loanData[selectedLoan])
    ) return false;
    return loanData[selectedLoan].allowedOperation === 'Request_Cancel';
  },
);

const selectPendingAddressesCount = () => createSelector(
  selectEditableMailingAddress(),
  (editablAddresses) => (Object.values(editablAddresses)
    .filter((address) => address.pending).length),
);

const selectPropertyAddress = (shortAddress = false) => createSelector(
  makeSelectLoans('selectedLoan'),
  makeSelectMain('loans'),
  (selectedLoan, loans) => {
    if (selectedLoan) {
      const { header } = loans.find(({ hashedLoanNumber }) =>
        hashedLoanNumber === selectedLoan) || {};
      const {
        address1 = '',
        city = '',
        postalCode = '',
        stateCode = '',
      } = header;

      return shortAddress ?
        `${capitalizeString(address1)}` :
        `${capitalizeString(address1)}, ${capitalizeString(city)}, ${stateCode} ${postalCode}`;
    }
    return '';
  },
);

const selectPropertyAddressesPendingFirst = () => createSelector(
  getLoanAddresses(),
  selectEditableMailingAddress(),
  (propertyAddresses, mailingAddressProps) => {
    if (isEmpty(mailingAddressProps)) return [];
    const pendingAddresses = [];
    const nonPendingAddresses = [];
    propertyAddresses.forEach((property) => {
      const { loan } = property;
      const { pending } = mailingAddressProps[loan] || {};
      if (pending) {
        pendingAddresses.push(property);
      } else {
        nonPendingAddresses.push(property);
      }
    });
    return pendingAddresses.concat(nonPendingAddresses);
  },
);

// Specifically for the ChangeMailingAddress flow
const selectRequestBody = () => createSelector(
  makeSelectLoans('loanData'),
  selectForm('changeMailingAddress'),
  selectFormattedCurrentSetting(),
  selectFormattedReadOnlyFields(),
  (loanData, form, currentSetting, readOnlyFields) => {
    if (!isEmpty(loanData)) {
      if (currentSetting && readOnlyFields) {
        const readOnlyFormValues = readOnlyFields.reduce((acc, field) => {
          acc[field] = currentSetting[field];
          return acc;
        }, {});
        return { ...form, ...readOnlyFormValues };
      }
    }
    return {};
  },
);

const selectMultiBorrowerChangeMailingJiraRequestBody = () => createSelector(
  selectFormValuesToSubmit(),
  selectForm('uploadDocument'),
  makeSelectLoans('selectedLoan'),
  (addressForm, documentForm, loanNumber) => {
    const { fileInput: { fileData, fileSize } } = documentForm;
    return {
      document: fileData,
      documentCategory: 'mailingAddress',
      documentSize: fileSize,
      formValues: addressForm,
      loanNumber,
    };
  },
);

const selectRequestStatus = (prop) => createSelector(
  makeSelectLoans('requestStatus'),
  (requestStatus) => requestStatus[prop],
);

/**
 * @param {(
  *  'hashedLoanNumber'|'maskedLoanNumber'|'unmaskedLoanNumber'
  * )} format - Format/version of Loans reducer's selectedLoan to return
  */
const selectSelectedLoan = (format = 'hashedLoanNumber') => createSelector(
  makeSelectLoans('selectedLoan'),
  makeSelectMain('loans'),
  (selectedLoan, loans) => {
    if (selectedLoan) {
      const { loanNumber, maskedLoanNumber } = loans.find(({ hashedLoanNumber }) =>
        hashedLoanNumber === selectedLoan);
      const dictionary = {
        hashedLoanNumber: selectedLoan,
        maskedLoanNumber,
        unmaskedLoanNumber: loanNumber,
      };
      return dictionary[format] || '';
    }
    return '';
  },
);

const selectServiceError = (prop) => createSelector(
  makeSelectLoans('errors'),
  ({ serviceErrors }) => serviceErrors[prop],
);

const selectUpdatedLoans = () => createSelector(
  makeSelectMain('loans'),
  makeSelectLoans('updatedPrimaryLoan'),
  (loans, primaryLoan) => {
    if (!primaryLoan) return [];
    const updatedLoans = [];
    loans.forEach((loan) => {
      if (loan.hashedLoanNumber === primaryLoan) {
        updatedLoans.unshift({ ...loan, isPrimaryLoan: true });
      } else {
        updatedLoans.push({ ...loan, isPrimaryLoan: false });
      }
    });
    return updatedLoans;
  },
);

const selectIsAnyLoanMancodeY = () => createSelector(
  makeSelectMain('selectedLoan'),
  makeSelectLoans('loanData'),
  (selectedLoan, loanData) => {
    if (!isEmpty(loanData)) {
      if (Object.keys(loanData).length > 1) {
        return !!Object.keys(loanData).some((key) => loanData[key].isLoanMancodeY);
      }
      return !!loanData[selectedLoan].isLoanMancodeY;
    }
    return false;
  },
);

/**
 * Default selector used by Loans
 */
const makeSelectLoans = (prop) => createSelector(
  selectLoansDomain,
  (state) => state[prop],
);

export default makeSelectLoans;
export {
  selectBannerError,
  selectCanUploadMailingAddress,
  selectEditableMailingAddress,
  selectForm,
  selectFormattedReadOnlyFields,
  selectFormErrors,
  selectFormToUse,
  selectFormValuesToSubmit,
  selectInternationalFormType,
  selectIsAnyLoanMancodeY,
  selectIsCountryProvinceStateDataFetched,
  selectIsPastServicingPaymentCutOffDate,
  selectIsSubmitMailingAddressChangeButtonDisabled,
  selectLoanMailingAddress,
  selectLoansScreenReaderNotifications,
  selectMailingAddressAddressee,
  selectIsMailingAddressCancelable,
  selectMultiBorrowerChangeMailingJiraRequestBody,
  selectPendingAddressesCount,
  selectPropertyAddress,
  selectPropertyAddressesPendingFirst,
  selectRequestBody,
  selectRequestStatus,
  selectSelectedLoan,
  selectServiceError,
  selectUpdatedLoans,
};
